import {selectUserSettingsData} from '@/redux/reducers/userSettingsReducer';
import {useDispatch, useSelector} from 'react-redux';

import TextLink from '@/atoms/Links/Text/TextLink';

import ProductCard from '@/molecules/Cards/Product/ProductCard';

import ProductCardSkeleton from '@/atoms/Skeletons/ProductCard/ProductCardSkeleton';
import useAuth from '@/hooks/use-auth/use-auth';
import {loadProductFavourites} from '@/redux/actions/productFavouriteActions';
import {STATUS_FAILED, STATUS_IDLE} from '@/redux/actionTypes';
import {selectProductFavouritesStatus} from '@/redux/reducers/productFavouritesReducer';
import {makeKebabCase} from '@/utils/helpers/string';
import {useEffect} from 'react';
import {StyledCardGridList, StyledHeader, StyledProductFeed} from './styles';
import {ProductFeedProps} from './typings';

const ProductFeed = ({
  title,
  description,
  viewAllLink,
  products,
  gaEvents,
  loading,
  skeletonCount = 4,
}: ProductFeedProps) => {
  const auth = useAuth();
  const dispatch = useDispatch();
  const userSettings = useSelector(selectUserSettingsData);
  const favouritesStatus = useSelector(selectProductFavouritesStatus);

  useEffect(() => {
    if (
      auth.isAuthenticated() &&
      (favouritesStatus === STATUS_IDLE || favouritesStatus === STATUS_FAILED)
    ) {
      dispatch(loadProductFavourites());
    }
  }, [auth.isAuthenticated()]);

  return (
    <StyledProductFeed>
      <StyledHeader>
        {title && <h2>{title}</h2>}

        {description && <p>{description}</p>}

        {viewAllLink && (
          <TextLink
            href={viewAllLink}
            title="View all"
            onClick={() => {
              if (gaEvents?.viewAll) gaEvents.viewAll();
            }}
          >
            View all
          </TextLink>
        )}
      </StyledHeader>

      <StyledCardGridList>
        {loading
          ? [...Array(skeletonCount)].map((_, i) => (
              <li key={`${makeKebabCase(title)}-product-feed-skeleton-${i}`}>
                <ProductCardSkeleton aspectRatio="1/1" announce={i === 0} />
              </li>
            ))
          : products?.map((product, index) => (
              <li key={`${makeKebabCase(title)}-product-feed-${product.id}`}>
                <ProductCard
                  product={product}
                  displaySizeUnits={userSettings.display_units}
                  displayCurrency={userSettings.currency}
                  currencySymbol={userSettings.currency_symbol}
                  lazyLoading={false}
                  respectAspectRatio={false}
                  gaEvents={{
                    click: () => {
                      if (gaEvents?.productClick) gaEvents.productClick(index + 1);
                    },
                    favourite: () => {
                      if (gaEvents?.productFavourite) gaEvents.productFavourite(index + 1);
                    },
                  }}
                />
              </li>
            ))}
      </StyledCardGridList>
    </StyledProductFeed>
  );
};

export default ProductFeed;
